// Dynamic imports to handle missing packages gracefully
let html2canvas = null;
let jsPDF = null;
let saveAs = null;

// Lazy load packages
const loadHtml2Canvas = async () => {
  if (!html2canvas) {
    try {
      html2canvas = (await import("html2canvas")).default;
    } catch (error) {
      throw new Error("html2canvas package not installed. Please run: npm install html2canvas");
    }
  }
  return html2canvas;
};

const loadJsPDF = async () => {
  if (!jsPDF) {
    try {
      jsPDF = (await import("jspdf")).default;
    } catch (error) {
      throw new Error("jspdf package not installed. Please run: npm install jspdf");
    }
  }
  return jsPDF;
};

const loadFileSaver = async () => {
  if (!saveAs) {
    try {
      saveAs = (await import("file-saver")).saveAs;
    } catch (error) {
      throw new Error("file-saver package not installed. Please run: npm install file-saver");
    }
  }
  return saveAs;
};

/**
 * Export tweet preview element to PNG
 * @param {HTMLElement} element - The DOM element to capture
 * @param {string} filename - Output filename
 * @param {number} scale - Scale factor for high DPI (default: 2)
 */
export const exportToPNG = async (element, filename = "tweet-screenshot.png", scale = 2) => {
  // Store original dimensions outside try block so they're accessible in catch
  let originalWidth = '';
  let originalHeight = '';
  let originalMinWidth = '';
  let originalMinHeight = '';
  
  try {
    if (!element) {
      throw new Error("Element not found");
    }

    const html2canvasLib = await loadHtml2Canvas();
    const saveAsLib = await loadFileSaver();

    // Scroll element into view to ensure it's fully rendered
    element.scrollIntoView({ behavior: 'instant', block: 'center' });
    
    // Ensure element has explicit dimensions for html2canvas
    originalWidth = element.style.width;
    originalHeight = element.style.height;
    originalMinWidth = element.style.minWidth;
    originalMinHeight = element.style.minHeight;
    
    // Set explicit dimensions if not already set
    if (!element.style.width) {
      element.style.width = `${element.scrollWidth}px`;
    }
    if (!element.style.height) {
      element.style.height = `${element.scrollHeight}px`;
    }
    element.style.minWidth = `${element.scrollWidth}px`;
    element.style.minHeight = `${element.scrollHeight}px`;
    
    // Wait for fonts to load
    await document.fonts.ready;
    
    // Wait for images to load
    const images = element.querySelectorAll('img');
    const imagePromises = Array.from(images).map((img) => {
      if (img.complete && img.naturalWidth > 0) {
        return Promise.resolve();
      }
      return new Promise((resolve) => {
        img.onload = resolve;
        img.onerror = resolve; // Resolve even on error to not block export
        // Timeout after 5 seconds
        setTimeout(resolve, 5000);
      });
    });
    
    await Promise.all(imagePromises);
    
    // Small delay to ensure rendering is complete
    await new Promise(resolve => setTimeout(resolve, 200));

    // Get computed background - check if it's a gradient or solid color
    const computedStyle = window.getComputedStyle(element);
    const bgImage = computedStyle.backgroundImage;
    const bgColor = computedStyle.backgroundColor;
    const bg = computedStyle.background; // This includes gradients
    
    // Determine background handling
    let backgroundColor = null;
    
    // Check if there's a gradient or image background
    if (bgImage && bgImage !== 'none' && bgImage !== 'initial') {
      // It's a gradient or image background - use null to let html2canvas capture it
      backgroundColor = null;
    } else if (bg && bg !== 'none' && bg !== 'initial' && bg.includes('gradient')) {
      // Explicit gradient in background property
      backgroundColor = null;
    } else if (bgColor && bgColor !== 'rgba(0, 0, 0, 0)' && bgColor !== 'transparent' && bgColor !== 'initial') {
      // Solid background color
      backgroundColor = bgColor;
    } else {
      // No background specified, use null to capture whatever is there
      backgroundColor = null;
    }
    
    // Configure html2canvas options for high quality
    const canvas = await html2canvasLib(element, {
      scale: scale, // Higher scale = better quality
      useCORS: true,
      logging: false, // Disable logging in production
      backgroundColor: backgroundColor, // null for gradients/images, color for solid backgrounds
      width: element.scrollWidth,
      height: element.scrollHeight,
      windowWidth: element.scrollWidth,
      windowHeight: element.scrollHeight,
      allowTaint: true, // Allow cross-origin images
      removeContainer: false,
      imageTimeout: 15000, // Wait up to 15 seconds for images
      onclone: (clonedDoc, element) => {
        // Ensure cloned document has proper styles
        const clonedElement = clonedDoc.getElementById('tweet-preview-export');
        if (clonedElement) {
          // Copy inline styles directly from original element (these include gradients)
          const originalInlineStyle = element.getAttribute('style') || '';
          if (originalInlineStyle) {
            clonedElement.setAttribute('style', originalInlineStyle);
          }
          
          // Also copy computed styles as fallback
          const originalStyles = window.getComputedStyle(element);
          
          // Force visibility and ensure styles are preserved
          clonedElement.style.visibility = 'visible';
          clonedElement.style.opacity = '1';
          clonedElement.style.display = 'block';
          
          // Copy background styles explicitly (inline styles take priority)
          if (element.style.background && element.style.background !== 'none') {
            clonedElement.style.background = element.style.background;
          } else if (originalStyles.background && originalStyles.background !== 'none') {
            clonedElement.style.background = originalStyles.background;
          }
          
          if (element.style.backgroundColor && element.style.backgroundColor !== 'rgba(0, 0, 0, 0)') {
            clonedElement.style.backgroundColor = element.style.backgroundColor;
          } else if (originalStyles.backgroundColor && originalStyles.backgroundColor !== 'rgba(0, 0, 0, 0)') {
            clonedElement.style.backgroundColor = originalStyles.backgroundColor;
          }
          
          if (element.style.backgroundImage && element.style.backgroundImage !== 'none') {
            clonedElement.style.backgroundImage = element.style.backgroundImage;
          } else if (originalStyles.backgroundImage && originalStyles.backgroundImage !== 'none') {
            clonedElement.style.backgroundImage = originalStyles.backgroundImage;
          }
          
          // Copy dimensions
          clonedElement.style.width = originalStyles.width || `${element.scrollWidth}px`;
          clonedElement.style.height = originalStyles.height || `${element.scrollHeight}px`;
          clonedElement.style.minWidth = `${element.scrollWidth}px`;
          clonedElement.style.minHeight = `${element.scrollHeight}px`;
          
          // Ensure all child images are visible and have proper dimensions
          const images = clonedElement.querySelectorAll('img');
          images.forEach(img => {
            img.style.visibility = 'visible';
            img.style.opacity = '1';
            img.style.display = 'block';
            
            // Ensure images maintain their natural dimensions
            if (img.complete && img.naturalWidth > 0) {
              img.style.width = img.style.width || `${img.naturalWidth}px`;
              img.style.height = img.style.height || `${img.naturalHeight}px`;
            }
          });
          
          // Ensure all text elements preserve their styles
          const textElements = clonedElement.querySelectorAll('*');
          textElements.forEach(el => {
            const elStyles = window.getComputedStyle(el);
            if (elStyles.color) el.style.color = elStyles.color;
            if (elStyles.fontSize) el.style.fontSize = elStyles.fontSize;
            if (elStyles.fontFamily) el.style.fontFamily = elStyles.fontFamily;
            if (elStyles.fontWeight) el.style.fontWeight = elStyles.fontWeight;
            if (elStyles.backgroundColor && elStyles.backgroundColor !== 'rgba(0, 0, 0, 0)') {
              el.style.backgroundColor = elStyles.backgroundColor;
            }
          });
        }
      },
    });

    // Convert canvas to blob
    canvas.toBlob((blob) => {
      // Restore original dimensions
      if (element) {
        element.style.width = originalWidth || '';
        element.style.height = originalHeight || '';
        element.style.minWidth = originalMinWidth || '';
        element.style.minHeight = originalMinHeight || '';
      }
      
      if (blob) {
        saveAsLib(blob, filename);
      } else {
        throw new Error("Failed to create blob");
      }
    }, "image/png");
  } catch (error) {
    console.error("Error exporting to PNG:", error);
    // Restore original dimensions on error
    if (element) {
      element.style.width = originalWidth || '';
      element.style.height = originalHeight || '';
      element.style.minWidth = originalMinWidth || '';
      element.style.minHeight = originalMinHeight || '';
    }
    throw error;
  }
};

/**
 * Export tweet preview element to PDF
 * @param {HTMLElement} element - The DOM element to capture
 * @param {string} filename - Output filename
 */
export const exportToPDF = async (element, filename = "tweet-screenshot.pdf") => {
  try {
    if (!element) {
      throw new Error("Element not found");
    }

    const html2canvasLib = await loadHtml2Canvas();
    const jsPDFLib = await loadJsPDF();

    // First convert to canvas
    const canvas = await html2canvasLib(element, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: "#ffffff",
      width: element.scrollWidth,
      height: element.scrollHeight,
    });

    const imgData = canvas.toDataURL("image/png");

    // Calculate PDF dimensions
    const imgWidth = canvas.width;
    const imgHeight = canvas.height;
    const pdfWidth = imgWidth * 0.75; // Convert pixels to mm (approximate)
    const pdfHeight = imgHeight * 0.75;

    // Create PDF
    const pdf = new jsPDFLib({
      orientation: pdfHeight > pdfWidth ? "portrait" : "landscape",
      unit: "px",
      format: [pdfWidth, pdfHeight],
    });

    // Add image to PDF
    pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);

    // Save PDF
    pdf.save(filename);
  } catch (error) {
    console.error("Error exporting to PDF:", error);
    throw error;
  }
};

/**
 * Generate filename from tweet data
 * @param {Object} tweetData - Tweet data object
 * @param {string} format - Export format ('png' | 'pdf')
 */
export const generateFilename = (tweetData, format = "png") => {
  if (!tweetData) {
    return `tweet-${Date.now()}.${format}`;
  }

  const username = tweetData.author?.username || "unknown";
  const tweetId = tweetData.id || Date.now();
  const date = new Date().toISOString().split("T")[0];

  return `tweet-${username}-${tweetId}-${date}.${format}`;
};

